import type { ButtonHTMLAttributes, MouseEvent } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';
import {
  backgroundColor,
  borderTop,
  color,
  display,
  size,
  space,
  themeGet,
  width,
} from 'styled-system';

import { useLocalization } from '../../providers/LocaleProvider';
import type { Color } from '../../theme/colors/types';
import { boxSizing, fill, transform } from '../../theme/system-utilities';
import type { BaseIconProps } from '../Icon';
import Icon from '../Icon';
import BackButton from './BackButton';
import ProceedButton from './ProceedButton';

export interface NavigationProps {
  proceedButtonText: string;
  isProceedDisabled: boolean;
  isProceedHidden?: boolean;
  isProceedLoading?: boolean;
  previousHref?: Nullable<string>;
  previousAriaLabel?: string;
  proceedButtonType?: ButtonHTMLAttributes<unknown>['type'];
  onBackButtonClick?: (event: MouseEvent) => void;
  onProceedButtonClick?: (event: MouseEvent) => void;
}

const ICON_BACK_SIZE = 32;

const Container = styled.div<any>`
  ${backgroundColor};
  ${boxSizing};
  ${color};
  ${display};
  ${space};
  ${width};
  ${borderTop};
  box-shadow: 0 1px 8px 0 ${themeGet('colors.overlay')};
`;
const containerStyles = {
  backgroundColor: 'sys.neutral.background.default',
  boxSizing: 'border-box',
  borderTop: 3,
  display: 'flex',
  pl: ['sp_16', null, null, 'sp_20'],
  pr: 'sp_16',
  py: 'sp_8',
  width: 1,
};

const BackIcon = styled(Icon)<BaseIconProps>`
  ${size};
  ${fill};
  ${transform};
`;
const backIconStyles = {
  size: ICON_BACK_SIZE,
  transform: 'rotate(180deg)',
  fill: 'sys.neutral.text.default' as Color,
};

const Navigation = ({
  previousHref,
  previousAriaLabel,
  onBackButtonClick,
  onProceedButtonClick,
  proceedButtonType = 'submit',
  proceedButtonText,
  isProceedDisabled,
  isProceedHidden = false,
  isProceedLoading,
}: NavigationProps) => {
  const { t } = useLocalization();

  return (
    <Container {...containerStyles}>
      {!!previousHref && (
        <BackButton
          data-testid="navigation-back-link"
          px="sp_4"
          py="sp_0"
          aria-label={previousAriaLabel ?? t('h24_go_back')}
          href={previousHref}
          mr={['sp_8', 'sp_12']}
          onClick={onBackButtonClick}
        >
          <BackIcon name="arrowRight" {...backIconStyles} />
        </BackButton>
      )}
      {!isProceedHidden && (
        <ProceedButton
          data-testid="navigation-proceed-button"
          type={proceedButtonType}
          disabled={isProceedDisabled}
          isLoading={isProceedLoading}
          onClick={onProceedButtonClick}
        >
          {proceedButtonText}
        </ProceedButton>
      )}
    </Container>
  );
};

export default Navigation;
